import React, { forwardRef, useImperativeHandle, memo, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { StyleSheet, Platform, View, Text } from 'react-native';
import { Canvas, Path, Skia } from '@shopify/react-native-skia';
import { MermaidHierarchyLink, HierarchyPosition, MermaidEdgeStyle } from './types';

export interface MermaidLinkRendererHandle {
  forceUpdate: () => void;
}

export interface MermaidLinkRendererProps {
  itemPositionsRef: React.RefObject<Record<string, HierarchyPosition>>;
  links: MermaidHierarchyLink[];
  theme?: boolean;
  zoomLevel?: number;
}

/**
 * Default link renderer for Mermaid diagram edges
 * Supports different edge styles (solid, dashed, dotted) and labels
 * Uses Skia Canvas for native platforms
 */
const MermaidLinkRenderer = forwardRef<MermaidLinkRendererHandle, MermaidLinkRendererProps>(({
  links,
  itemPositionsRef,
  theme = true,
  zoomLevel = 1
}, ref) => {
  const componentMountedRef = useRef(true);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Immediate update for real-time tracking
  const immediateUpdate = useCallback(() => {
    if (componentMountedRef.current) {
      setUpdateTrigger(prev => prev + 1);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    forceUpdate() {
      immediateUpdate();
    }
  }));

  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  // Force update when positions become available
  useEffect(() => {
    const currentPositions = itemPositionsRef.current;
    if (currentPositions && Object.keys(currentPositions).length > 0 && links.length > 0) {
      // Check if we have positions for all links
      const allLinksHavePositions = links.every(link => {
        const source = currentPositions[link.sourceId];
        const target = currentPositions[link.targetId];
        return source && target;
      });

      if (allLinksHavePositions) {
        // Positions are available, force update
        immediateUpdate();
      }
    }
  }, [links, itemPositionsRef, immediateUpdate]);

  // Get link color based on type and theme
  const getLinkColor = useCallback((link: MermaidHierarchyLink): string => {
    if (link.color) return link.color;
    
    // Default colors based on link type
    if (link.type === 'async') return theme ? '#3B82F6' : '#60A5FA';
    if (link.type === 'sync') return theme ? '#10B981' : '#34D399';
    if (link.type === 'inheritance') return theme ? '#8B5CF6' : '#A78BFA';
    if (link.type === 'transition') return theme ? '#F59E0B' : '#FBBF24';
    if (link.type === 'relationship') return theme ? '#EF4444' : '#F87171';
    
    return theme ? '#6B7280' : '#9CA3AF';
  }, [theme]);

  // Convert links to Skia paths
  const linkPaths = useMemo(() => {
    if (!links?.length) {
      if (__DEV__) {
        console.log('🔗 MermaidLinkRenderer: No links provided');
      }
      return [];
    }

    const currentPositions = itemPositionsRef.current;
    if (!currentPositions || Object.keys(currentPositions).length === 0) {
      if (__DEV__) {
        console.log('🔗 MermaidLinkRenderer: No positions available yet', {
          hasRef: !!itemPositionsRef,
          hasCurrent: !!currentPositions,
          keysCount: currentPositions ? Object.keys(currentPositions).length : 0
        });
      }
      return [];
    }

    if (__DEV__) {
      console.log('🔗 MermaidLinkRenderer: Processing links', {
        linksCount: links.length,
        positionsCount: Object.keys(currentPositions).length,
        positionKeys: Object.keys(currentPositions),
        linkSourceIds: links.map(l => l.sourceId),
        linkTargetIds: links.map(l => l.targetId)
      });
    }

    const paths: Array<{
      id: string;
      curvePath: ReturnType<typeof Skia.Path.MakeFromSVGString> | null;
      arrowheadPath: ReturnType<typeof Skia.Path.Make> | null;
      color: string;
      label?: string;
      midPoint: { x: number; y: number };
      isDashed: boolean;
    }> = [];

    links.forEach((link, i) => {
      try {
        const sourceId = link.sourceId;
        const targetId = link.targetId;
        const source = currentPositions[sourceId];
        const target = currentPositions[targetId];

        if (!source || !target) {
          if (__DEV__) {
            console.warn('🔗 MermaidLinkRenderer: Missing positions for link', {
              sourceId,
              targetId,
              hasSource: !!source,
              hasTarget: !!target,
              availableIds: Object.keys(currentPositions)
            });
          }
          return; // Skip if positions not available
        }

        // Calculate connection points (center of nodes for simplicity)
        // In a real implementation, you'd calculate based on node bounds
        const sourceX = source.x + 80; // Assume node width ~160
        const sourceY = source.y + 40; // Assume node height ~80
        const targetX = target.x + 80;
        const targetY = target.y + 40;

        // Calculate control points for bezier curve
        const dx = targetX - sourceX;
        const dy = targetY - sourceY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Handle zero or very small distance
        if (distance < 1) {
          if (__DEV__) {
            console.warn('🔗 MermaidLinkRenderer: Nodes too close together, skipping link', {
              sourceId,
              targetId,
              source: { x: sourceX, y: sourceY },
              target: { x: targetX, y: targetY },
              distance
            });
          }
          return;
        }
        
        const controlOffset = Math.min(distance * 0.5, 200);

        const controlX1 = sourceX + dx * 0.5 + (dy / distance) * controlOffset;
        const controlY1 = sourceY + dy * 0.5 - (dx / distance) * controlOffset;
        const controlX2 = targetX - dx * 0.5 + (dy / distance) * controlOffset;
        const controlY2 = targetY - dy * 0.5 - (dx / distance) * controlOffset;

        // Create bezier curve path
        const pathString = `M ${sourceX} ${sourceY} C ${controlX1} ${controlY1} ${controlX2} ${controlY2} ${targetX} ${targetY}`;
        const curvePath = Skia.Path.MakeFromSVGString(pathString);
        
        if (!curvePath) {
          return;
        }

        // Create arrowhead
        const arrowheadPath = Skia.Path.Make();
        const arrowSize = 12;
        const angle = Math.atan2(dy, dx);
        
        // Calculate arrowhead points
        const tipX = targetX;
        const tipY = targetY;
        const base1X = tipX - arrowSize * Math.cos(angle - Math.PI / 6);
        const base1Y = tipY - arrowSize * Math.sin(angle - Math.PI / 6);
        const base2X = tipX - arrowSize * Math.cos(angle + Math.PI / 6);
        const base2Y = tipY - arrowSize * Math.sin(angle + Math.PI / 6);
        
        arrowheadPath.moveTo(tipX, tipY);
        arrowheadPath.lineTo(base1X, base1Y);
        arrowheadPath.lineTo(base2X, base2Y);
        arrowheadPath.close();

        // Calculate midpoint for label
        const midPoint = {
          x: (sourceX + targetX) / 2,
          y: (sourceY + targetY) / 2,
        };

        // Determine if dashed based on edge style
        const isDashed = link.edgeStyle === 'dashed' || link.edgeStyle === 'dotted';

        const color = getLinkColor(link);

        paths.push({
          id: `${sourceId}-${targetId}-${i}`,
          curvePath,
          arrowheadPath,
          color,
          label: link.label,
          midPoint,
          isDashed,
        });

        if (__DEV__) {
          console.log('✅ MermaidLinkRenderer: Created path', {
            id: `${sourceId}-${targetId}-${i}`,
            source: { x: sourceX, y: sourceY },
            target: { x: targetX, y: targetY },
            color
          });
        }
      } catch (error) {
        if (__DEV__) {
          console.warn('❌ Error processing Mermaid link', i, error);
        }
      }
    });

    if (__DEV__) {
      console.log('🎨 MermaidLinkRenderer: Created paths', paths.length);
    }

    return paths;
  }, [links, itemPositionsRef, updateTrigger, getLinkColor]);

  if (linkPaths.length === 0) {
    return null;
  }

  // Render with Skia Canvas for native platforms
  if (Platform.OS !== 'web') {
    return (
      <Canvas style={styles.canvas} pointerEvents="none">
        {linkPaths.map(({ id, curvePath, arrowheadPath, color, isDashed }) => {
          try {
            return (
              <React.Fragment key={id}>
                {curvePath && (
                  <Path
                    path={curvePath}
                    color={color}
                    style="stroke"
                    strokeWidth={2}
                    strokeCap="round"
                    strokeJoin="round"
                    opacity={0.8}
                  />
                )}
                {arrowheadPath && (
                  <Path
                    path={arrowheadPath}
                    color={color}
                    style="fill"
                  />
                )}
              </React.Fragment>
            );
          } catch (error) {
            if (__DEV__) {
              console.warn('Error rendering Mermaid link', id, error);
            }
            return null;
          }
        })}
      </Canvas>
    );
  }

  // Web fallback - render with SVG
  return (
    <View style={styles.canvas} pointerEvents="none">
      {/* Web implementation would use react-native-svg */}
      {/* For now, return null on web - can be enhanced later */}
      {__DEV__ && (
        <Text style={{ color: 'red', fontSize: 10 }}>
          Web SVG rendering not yet implemented
        </Text>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0, // Behind nodes
  },
});

MermaidLinkRenderer.displayName = 'MermaidLinkRenderer';

export default memo(MermaidLinkRenderer, (prevProps, nextProps) => {
  // Re-render if essential props change
  // Note: We don't check positions here because they're in a ref and checked in useMemo
  const shouldUpdate = !(
    prevProps.links.length === nextProps.links.length &&
    prevProps.theme === nextProps.theme &&
    prevProps.zoomLevel === nextProps.zoomLevel &&
    prevProps.itemPositionsRef === nextProps.itemPositionsRef
  );
  
  if (__DEV__ && shouldUpdate) {
    console.log('🔄 MermaidLinkRenderer: Re-rendering due to prop change');
  }
  
  return !shouldUpdate;
});

